import { runInAction } from 'mobx';
import { logger } from '../../../../utils/logger';
import { addMonetaryValues, compareMonetaryValue, getZeroMonetaryValue, isZeroMonetaryValue, multiplyMonetaryValue } from '../../../../utils/monetary-value-utils';
import { updateObject } from '../../../../utils/object-utils';
import { getItemModifierGroupById } from '../../../sales-data/selectors/items/get-item-modifier-group-by-id';
import { getDefaultItemPrice } from '../../../sales-data/utils/items/get-default-item-price';
import { isInheritedDiscountItemPrice } from '../../../sales-data/utils/items/item-price-utils';
import { updateInProgressItemIsValid } from './update-in-progress-item-is-valid';
import { updateInProgressItemTotalPrice } from './update-in-progress-item-total-price';
import { updateInProgressItemTotalSavings } from './update-in-progress-item-total-savings';
export const validateAndUpdateInProgressItemModifierItemGroup = (inProgressItem, inProgressItemModifierGroupId, inProgressItemModifierSelections) => {
    const inProgressItemModifierGroup = inProgressItem.modifierGroups.find(mg => mg.itemModifierGroupId === inProgressItemModifierGroupId);
    if (inProgressItemModifierGroup === undefined) {
        throw new Error(`Could not find modifier group ${inProgressItemModifierGroupId} on in-progress item ${inProgressItem.id}`);
    }
    const validationResult = validateInProgressItemModifiers(inProgressItem, inProgressItemModifierGroupId, inProgressItemModifierSelections);
    if (!validationResult.isValid) {
        logger.error(validationResult.errorDescription);
    }
    runInAction(() => {
        updateObject(inProgressItemModifierGroup, validationResult.inProgressItemModifierGroup);
    });
    updateInProgressItemIsValid(inProgressItem);
    updateInProgressItemTotalSavings(inProgressItem);
    updateInProgressItemTotalPrice(inProgressItem);
};
const validateInProgressItemModifiers = (inProgressItem, inProgressItemModifierGroupId, inProgressItemModifierSelections) => {
    const inProgressItemModifierGroup = inProgressItem.modifierGroups.find(mg => mg.itemModifierGroupId === inProgressItemModifierGroupId);
    const normalItemModifierGroup = inProgressItem.item.modifierGroups.find(mg => mg.itemModifierGroupId === inProgressItemModifierGroupId);
    if (inProgressItemModifierGroup === undefined || normalItemModifierGroup === undefined) {
        throw new Error(`Could not find modifier group with ID: ${inProgressItemModifierGroupId} on in-progress item with ID: ${inProgressItem.id}`);
    }
    const modifierGroup = getItemModifierGroupById(normalItemModifierGroup.itemModifierGroupId);
    if (!modifierGroup)
        throw new Error(`Could not find modifier group with ID: ${inProgressItemModifierGroupId}`);
    return validateModifiersForItemModifierGroup(modifierGroup, inProgressItemModifierSelections);
};
const validateModifiersForItemModifierGroup = (modifierGroup, modifierSelections) => {
    const { quantityLimit } = modifierGroup;
    const selectedQuantity = modifierSelections.reduce((sum, modifier) => sum + modifier.quantity, 0);
    const minimumReached = selectedQuantity >= quantityLimit.minimum;
    const maximumReached = !quantityLimit.isUnlimited && selectedQuantity >= quantityLimit.maximum;
    const maximumExceeded = !quantityLimit.isUnlimited && selectedQuantity > quantityLimit.maximum;
    const updatedInProgressItemModifiers = getUpdatedInProgressModifiers(modifierGroup, modifierSelections);
    const inProgressItemModifierGroup = {
        itemModifierGroupId: modifierGroup.id,
        selectedQuantity,
        minimumReached,
        maximumReached,
        maximumExceeded,
        modifiers: updatedInProgressItemModifiers,
        totalPrice: addMonetaryValues(...updatedInProgressItemModifiers.map(modifier => modifier.totalPrice)),
        totalSavings: addMonetaryValues(...updatedInProgressItemModifiers.map(modifier => modifier.totalSavings))
    };
    const validModifierItemIdSet = new Set(modifierGroup.modifiers.map(m => m.itemId));
    for (const modifier of modifierSelections) {
        if (!validModifierItemIdSet.has(modifier.itemId)) {
            return {
                isValid: false,
                errorDescription: `Item ${modifier.itemId} is not part of the modifier group`,
                inProgressItemModifierGroup
            };
        }
    }
    if (!minimumReached) {
        return {
            isValid: false,
            errorDescription: 'Minimum modifier quantity not reached',
            inProgressItemModifierGroup
        };
    }
    if (maximumExceeded) {
        return {
            isValid: false,
            errorDescription: 'Maximum modifier quantity exceeded',
            inProgressItemModifierGroup
        };
    }
    return {
        isValid: true,
        inProgressItemModifierGroup
    };
};
const getUpdatedInProgressModifiers = (modifierGroup, modifierSelections) => {
    const { includedQuantity } = modifierGroup;
    const selectedModifierMap = new Map();
    modifierSelections.forEach(modifierSelection => {
        selectedModifierMap.set(modifierSelection.itemId, modifierSelection);
    });
    let remainingFreeQuantity = includedQuantity || 0;
    const updatedInProgressItemModifiers = [];
    // Free quantity should apply to cheapest paid modifiers first
    // So sort modifiers by price ascending
    const modifiers = [...modifierGroup.modifiers];
    modifiers.sort((a, b) => compareMonetaryValue(getSelectedModifierPrice(a, selectedModifierMap.get(a.itemId)).price, getSelectedModifierPrice(b, selectedModifierMap.get(b.itemId)).price));
    // Apply the included quantity to the modifiers, cheapest paid modifiers first
    modifiers.forEach(m => {
        const itemModifierSelection = selectedModifierMap.get(m.itemId);
        if (itemModifierSelection) {
            let freeQuantity = 0;
            const modifierPrice = getSelectedModifierPrice(m, itemModifierSelection);
            if (!isZeroMonetaryValue(modifierPrice.price)) {
                freeQuantity = Math.min(itemModifierSelection.quantity, remainingFreeQuantity);
                remainingFreeQuantity -= freeQuantity;
            }
            const paidQuantity = itemModifierSelection.quantity - freeQuantity;
            updatedInProgressItemModifiers.push({
                itemId: m.itemId,
                quantity: itemModifierSelection.quantity,
                freeQuantity,
                selectedPrice: modifierPrice,
                totalPrice: getTotalPriceForModifier(modifierPrice, paidQuantity),
                totalSavings: getTotalSavingsForModifier(modifierPrice, paidQuantity)
            });
        }
    });
    return updatedInProgressItemModifiers;
};
const getTotalPriceForModifier = (itemPrice, quantity) => multiplyMonetaryValue(itemPrice.price, quantity);
const getTotalSavingsForModifier = (itemPrice, quantity) => isInheritedDiscountItemPrice(itemPrice)
    ? multiplyMonetaryValue(itemPrice.discount.amountSaved, quantity)
    : getZeroMonetaryValue();
const getSelectedModifierPrice = (modifier, modifierSelection) => { var _a; return (_a = modifierSelection === null || modifierSelection === void 0 ? void 0 : modifierSelection.selectedPrice) !== null && _a !== void 0 ? _a : getDefaultItemPrice(modifier.prices); };
